import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import { 
  Save as SaveIcon, 
  Storage as DatabaseIcon,
  CheckCircle as TestIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface DatabaseConnection {
  server: string;
  port: number;
  database: string;
  username: string;
  password: string;
  useSSL: boolean;
}

interface SystemConfig {
  postgresConnection: DatabaseConnection;
  sqlServerConnection: DatabaseConnection;
  maxFileSizeMB: number;
  allowedExtensions: string;
  autoProcess: boolean;
  emailNotifications: boolean;
}

const Configuracion: React.FC = () => {
  const [settings, setSettings] = React.useState<SystemConfig>({
    postgresConnection: {
      server: '',
      port: 5432,
      database: '',
      username: '',
      password: '',
      useSSL: true,
    },
    sqlServerConnection: {
      server: 'localhost',
      port: 1433,
      database: 'master',
      username: 'sa',
      password: 'dummy_password_123',
      useSSL: false,
    },
    maxFileSizeMB: 50,
    allowedExtensions: '.xlsx,.xls',
    autoProcess: true,
    emailNotifications: false,
  });

  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<{ type: 'success' | 'error' | 'info', text: string } | null>(null);
  const [expandedSection, setExpandedSection] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await apiService.getConfiguration();
      if (response.config) {
        // Asegurar que SQL Server tenga valores válidos por defecto
        const configWithDefaults = {
          ...response.config,
          sqlServerConnection: {
            server: 'localhost',
            port: 1433,
            database: 'master',
            username: 'sa',
            password: 'dummy_password_123', // Valor aleatorio
            useSSL: false,
          }
        };
        setSettings(configWithDefaults);
      }
      
      // Mostrar mensaje informativo si es configuración por defecto
      if (response.message && response.message.includes('por defecto')) {
        setMessage({ type: 'info', text: response.message });
      }
    } catch (error) {
      console.error('Error al cargar la configuración:', error);
      setMessage({ type: 'error', text: 'Error al cargar la configuración' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage({ type: 'info', text: 'Guardando configuración completa...' });
      
      // Validar que al menos PostgreSQL esté configurado
      if (!settings.postgresConnection.server) {
        setMessage({ type: 'error', text: 'Debe configurar al menos la conexión a PostgreSQL' });
        return;
      }
      
      // Crear una copia de la configuración con valores válidos para SQL Server
      const configToSave = {
        ...settings,
        sqlServerConnection: {
          server: 'localhost', // Valor válido por defecto
          port: 1433,
          database: 'master', // Valor válido por defecto
          username: 'sa', // Valor válido por defecto
          password: 'dummy_password_123', // Valor aleatorio para evitar validación
          useSSL: false
        }
      };
      
      const response = await apiService.saveConfiguration(configToSave);
      setMessage({ 
        type: response.isValid ? 'success' : 'error', 
        text: response.message 
      });
      
      // Recargar configuración después de guardar
      if (response.isValid) {
        await loadConfiguration();
      }
    } catch (error) {
      console.error('Error al guardar la configuración:', error);
      setMessage({ type: 'error', text: 'Error al guardar la configuración' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async (type: 'postgres') => {
    try {
      setLoading(true);
      const connection = settings.postgresConnection;
      const response = await apiService.testConnection(type, connection);
      setMessage({ 
        type: response.isSuccess ? 'success' : 'error', 
        text: response.message 
      });
    } catch (error) {
      console.error(`Error al probar conexión ${type}:`, error);
      setMessage({ type: 'error', text: `Error al probar conexión ${type}` });
    } finally {
      setLoading(false);
    }
  };

  const updateConnection = (type: 'postgres' | 'sqlserver', field: keyof DatabaseConnection, value: any) => {
    setSettings(prev => ({
      ...prev,
      [type === 'postgres' ? 'postgresConnection' : 'sqlServerConnection']: {
        ...prev[type === 'postgres' ? 'postgresConnection' : 'sqlServerConnection'],
        [field]: value
      }
    }));
  };

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom color="primary">
        Configuración
      </Typography>

      <Collapse in={!!message}>
        <Alert 
          severity={message?.type} 
          onClose={() => setMessage(null)}
          sx={{ mb: 2 }}
        >
          {message?.text}
        </Alert>
      </Collapse>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              {/* Configuración de Base de Datos */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DatabaseIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Conexión
                </Typography>
                <IconButton 
                  onClick={() => toggleSection('database')}
                  sx={{ ml: 'auto' }}
                >
                  {expandedSection === 'database' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedSection === 'database'}>
                {/* PostgreSQL */}
                <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                  Glowix (conexión principal)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Servidor"
                      value={settings.postgresConnection.server}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Puerto"
                      type="number"
                      value={settings.postgresConnection.port}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Base de Datos"
                      value={settings.postgresConnection.database}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Usuario"
                      value={settings.postgresConnection.username}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contraseña"
                      type="password"
                      value={settings.postgresConnection.password}
                      margin="normal"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.postgresConnection.useSSL}
                          disabled
                        />
                      }
                      label="Usar SSL"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => handleTestConnection('postgres')}
                        disabled={loading || !settings.postgresConnection.server}
                        startIcon={<TestIcon />}
                      >
                        Validar conexión
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Collapse>

              {/* 
              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <UploadIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Importación
                </Typography>
                <IconButton 
                  onClick={() => toggleSection('import')}
                  sx={{ ml: 'auto' }}
                >
                  {expandedSection === 'import' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedSection === 'import'}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tamaño máximo de archivo (MB)"
                      type="number"
                      value={settings.maxFileSizeMB}
                      onChange={(e) => setSettings({...settings, maxFileSizeMB: parseInt(e.target.value)})}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Extensiones permitidas"
                      value={settings.allowedExtensions}
                      onChange={(e) => setSettings({...settings, allowedExtensions: e.target.value})}
                      margin="normal"
                      helperText="Separadas por comas"
                    />
                  </Grid>
                </Grid>
              </Collapse>
              */}
              {/* SECCIÓN OCULTA: Opciones Generales - Comentada para uso futuro */}
              {/*
              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Opciones Generales
                </Typography>
                <IconButton 
                  onClick={() => toggleSection('general')}
                  sx={{ ml: 'auto' }}
                >
                  {expandedSection === 'general' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedSection === 'general'}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.autoProcess}
                          onChange={(e) => setSettings({...settings, autoProcess: e.target.checked})}
                        />
                      }
                      label="Procesamiento automático de archivos"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.emailNotifications}
                          onChange={(e) => setSettings({...settings, emailNotifications: e.target.checked})}
                        />
                      }
                      label="Notificaciones por email"
                    />
                  </Grid>
                </Grid>
              </Collapse>
              */}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Sistema
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Versión: 1.0.0
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Última actualización: {new Date().toLocaleDateString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Botón Guardar alineado con el Grid md={8} */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handleSave}
              disabled={loading}
              startIcon={<SaveIcon />}
              sx={{ minWidth: '200px' }}
            >
              Guardar
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          {/* Espacio vacío para mantener la alineación */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Configuracion;
